import { SearchOutlined, StarBorderRounded, StarRounded } from '@mui/icons-material';
import {
    Box,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    InputAdornment,
    Paper,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import Button from '@mui/material/Button';
import { type FC, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import Result from 'components/v2/common/Result';

import api from 'api';
import useTranslate from 'hooks/useTranslate';
import { setAlert } from 'redux/alert';
import type {
    AdminConsultant,
    ConsultantServiceProviderAssignment,
    ConsultantServiceProviderChange,
    ServiceProviderAdminRow,
} from 'types';
import { getErrorMessage } from 'utils/v2/errorHelper';
import { getAdminConsultantName } from 'views/v2/adminPanel/helpers/adminConsultants';

interface ConsultantServiceProvidersDialogProps {
    consultant: AdminConsultant;
    providers: ServiceProviderAdminRow[];
    onClose: () => void;
    onAssignmentUpdated: (
        consultantId: number,
        provider: ServiceProviderAdminRow,
        update: ConsultantServiceProviderChange
    ) => void;
}

export const ConsultantServiceProvidersDialog: FC<ConsultantServiceProvidersDialogProps> = ({
    consultant,
    providers,
    onClose,
    onAssignmentUpdated,
}) => {
    const dispatch = useDispatch();
    const { translate } = useTranslate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const [search, setSearch] = useState('');
    const [assignments, setAssignments] = useState<ConsultantServiceProviderAssignment[]>(
        consultant.service_providers
    );
    const [updatingProviderIds, setUpdatingProviderIds] = useState<Set<number>>(new Set());

    const visibleProviders = useMemo(() => {
        const query = search.trim().toLowerCase();
        return providers
            .filter((provider) => !query || provider.company_name.toLowerCase().includes(query))
            .sort((left, right) => left.company_name.localeCompare(right.company_name));
    }, [providers, search]);

    const assignmentsByProviderId = useMemo(
        () =>
            new Map(assignments.map((assignment) => [assignment.service_provider_id, assignment])),
        [assignments]
    );
    const defaultCount = assignments.filter(
        (assignment) => assignment.is_default_consultant
    ).length;

    const setProviderUpdating = (providerId: number, updating: boolean) => {
        setUpdatingProviderIds((current) => {
            const next = new Set(current);
            if (updating) next.add(providerId);
            else next.delete(providerId);
            return next;
        });
    };

    const updateAssignment = async (
        provider: ServiceProviderAdminRow,
        update: ConsultantServiceProviderChange
    ) => {
        if (updatingProviderIds.has(provider.id)) return;
        setProviderUpdating(provider.id, true);
        const currentAssignment = assignmentsByProviderId.get(provider.id);
        const defaultChanged =
            update.assigned &&
            update.is_default_consultant !== Boolean(currentAssignment?.is_default_consultant);
        let linkedDuringUpdate = false;

        try {
            if (!update.assigned) {
                await api.serviceProvider.unlinkConsultant(provider.id, consultant.id);
            } else {
                if (!currentAssignment) {
                    await api.serviceProvider.linkConsultant(provider.id, consultant.id);
                    linkedDuringUpdate = true;
                }

                if (defaultChanged) {
                    if (update.is_default_consultant) {
                        await api.serviceProvider.setDefaultConsultant(provider.id, consultant.id);
                    } else {
                        await api.serviceProvider.removeDefaultConsultant(
                            provider.id,
                            consultant.id
                        );
                    }
                }
            }

            setAssignments((current) => {
                const withoutProvider = current.filter(
                    (assignment) => assignment.service_provider_id !== provider.id
                );
                return update.assigned
                    ? [
                          ...withoutProvider,
                          {
                              service_provider_id: provider.id,
                              company_name: provider.company_name,
                              is_default_consultant: update.is_default_consultant,
                          },
                      ]
                    : withoutProvider;
            });
            onAssignmentUpdated(consultant.id, provider, update);
            dispatch(
                setAlert({
                    message: translate(
                        defaultChanged
                            ? 'consultantDefaultUpdated'
                            : 'consultantProviderAssignmentsUpdated'
                    ),
                    type: 'success',
                })
            );
        } catch (error) {
            let rollbackFailed = false;
            if (linkedDuringUpdate && update.is_default_consultant) {
                try {
                    await api.serviceProvider.unlinkConsultant(provider.id, consultant.id);
                } catch {
                    rollbackFailed = true;
                }
            }
            if (rollbackFailed) {
                const reconciledUpdate = {
                    assigned: true,
                    is_default_consultant: false,
                };
                setAssignments((current) => [
                    ...current.filter(
                        (assignment) => assignment.service_provider_id !== provider.id
                    ),
                    {
                        service_provider_id: provider.id,
                        company_name: provider.company_name,
                        is_default_consultant: false,
                    },
                ]);
                onAssignmentUpdated(consultant.id, provider, reconciledUpdate);
            }
            dispatch(
                setAlert({
                    message: getErrorMessage(error, translate('somethingWentWrong')),
                    type: 'error',
                })
            );
        } finally {
            setProviderUpdating(provider.id, false);
        }
    };

    const assignmentControls = (provider: ServiceProviderAdminRow) => {
        const assignment = assignmentsByProviderId.get(provider.id);
        const isAssigned = Boolean(assignment);
        const isDefault = Boolean(assignment?.is_default_consultant);
        const isUpdating = updatingProviderIds.has(provider.id);

        return (
            <Stack
                direction="row"
                spacing={1.5}
                sx={{
                    alignItems: 'center',
                    justifyContent: { xs: 'space-between', md: 'flex-end' },
                }}
            >
                <FormControlLabel
                    label={translate('assigned')}
                    control={
                        <Switch
                            size="small"
                            checked={isAssigned}
                            disabled={isUpdating}
                            slotProps={{
                                input: {
                                    'aria-label': `${translate('assigned')} ${
                                        provider.company_name
                                    }`,
                                },
                            }}
                            onChange={(_event, checked) =>
                                void updateAssignment(provider, {
                                    assigned: checked,
                                    is_default_consultant: checked ? isDefault : false,
                                })
                            }
                        />
                    }
                    sx={{
                        m: 0,
                        '& .MuiFormControlLabel-label': {
                            fontSize: '0.8rem',
                            color: 'text.secondary',
                        },
                    }}
                />
                <Tooltip title={translate('defaultConsultantAssignmentHint')} arrow>
                    <FormControlLabel
                        label={translate('default')}
                        control={
                            <Checkbox
                                size="small"
                                checked={isDefault}
                                disabled={isUpdating}
                                icon={<StarBorderRounded fontSize="small" />}
                                checkedIcon={<StarRounded fontSize="small" />}
                                slotProps={{
                                    input: {
                                        'aria-label': `${translate('defaultConsultant')} ${
                                            provider.company_name
                                        }`,
                                    },
                                }}
                                onChange={(_event, checked) =>
                                    void updateAssignment(provider, {
                                        assigned: true,
                                        is_default_consultant: checked,
                                    })
                                }
                            />
                        }
                        sx={{
                            m: 0,
                            '& .MuiFormControlLabel-label': {
                                fontSize: '0.8rem',
                                color: isDefault ? 'primary.main' : 'text.secondary',
                            },
                        }}
                    />
                </Tooltip>
                {isUpdating ? <CircularProgress size={16} /> : <Box sx={{ width: 16 }} />}
            </Stack>
        );
    };

    const hasUpdates = updatingProviderIds.size > 0;

    return (
        <Dialog
            open
            fullWidth
            maxWidth="md"
            fullScreen={fullScreen}
            scroll="paper"
            onClose={hasUpdates ? undefined : onClose}
        >
            <DialogTitle>
                <Typography
                    component="span"
                    variant="h6"
                    sx={{ display: 'block', fontWeight: 700 }}
                >
                    {translate('manageConsultantServiceProviders')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {getAdminConsultantName(consultant)}
                </Typography>
            </DialogTitle>
            <DialogContent
                dividers
                sx={{ p: 0, display: 'flex', flexDirection: 'column', minHeight: 420 }}
            >
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1.5}
                    sx={{
                        px: { xs: 2, sm: 3 },
                        py: 1.5,
                        alignItems: { xs: 'stretch', sm: 'center' },
                        bgcolor: 'background.paper',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                    }}
                >
                    <TextField
                        size="small"
                        label={translate('searchServiceProviders')}
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        sx={{ width: { xs: '100%', sm: 300 } }}
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchOutlined sx={{ fontSize: 18 }} />
                                    </InputAdornment>
                                ),
                            },
                        }}
                    />
                    <Box sx={{ flex: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                        <Box component="span" sx={{ color: 'text.primary', fontWeight: 700 }}>
                            {assignments.length}
                        </Box>{' '}
                        {translate('assigned')} ·{' '}
                        <Box component="span" sx={{ color: 'primary.main', fontWeight: 700 }}>
                            {defaultCount}
                        </Box>{' '}
                        {translate('defaults')}
                    </Typography>
                </Stack>

                {!visibleProviders.length ? (
                    <Box sx={{ p: 3 }}>
                        <Result
                            title={translate('noServiceProvidersFound')}
                            description={translate('tryAdjustingSearch')}
                        />
                    </Box>
                ) : isMobile ? (
                    <Stack spacing={1} sx={{ p: 2, overflowY: 'auto' }}>
                        {visibleProviders.map((provider) => (
                            <Paper
                                key={provider.id}
                                variant="outlined"
                                sx={{ p: 1.25, borderRadius: 1.5 }}
                            >
                                <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.75 }}>
                                    {provider.company_name}
                                </Typography>
                                {assignmentControls(provider)}
                            </Paper>
                        ))}
                    </Stack>
                ) : (
                    <TableContainer sx={{ flex: 1, overflowY: 'auto' }}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 700 }}>
                                        {translate('serviceProvider')}
                                    </TableCell>
                                    <TableCell align="right" sx={{ width: 340, fontWeight: 700 }}>
                                        {translate('assignment')}
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {visibleProviders.map((provider) => (
                                    <TableRow key={provider.id} hover>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {provider.company_name}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            {assignmentControls(provider)}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 1.5 }}>
                <Button variant="outlined" onClick={onClose} disabled={hasUpdates}>
                    {translate('close')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ConsultantServiceProvidersDialog;
